/* HAZ Motorsport Part Finder — vanilla JS, no dependencies.
   Powers: cascading Make→Year→Model→Engine pickers (.pf-picker),
   garage chip (.pf-chip), product fitment badge (.pf-badge),
   results page (#pf-results). Vehicle persisted in localStorage. */
(() => {
  'use strict';
  if (window.PF) return;

  const LS_KEY = 'haz_vehicle';
  const RESULTS_URL = '/search?view=pf'; // alternate search template — theme-scoped, no store page needed
  const CURRENT_YEAR = new Date().getFullYear(); // open-ended "to present" ranges run to this year
  const PAGE_SIZE = 24;

  const PF = (window.PF = {
    get vehicle() {
      try {
        const v = JSON.parse(localStorage.getItem(LS_KEY));
        // garages saved before vehicle ids became opaque (a bare number) can't be matched any more: ask again
        if (v && !/^[0-9a-f]{10}$/.test(String(v.vid || ''))) { localStorage.removeItem(LS_KEY); return null; }
        return v;
      } catch (e) { return null; }
    },
    set vehicle(v) {
      if (v) localStorage.setItem(LS_KEY, JSON.stringify(v));
      else localStorage.removeItem(LS_KEY);
      document.dispatchEvent(new CustomEvent('pf:vehicle', { detail: v }));
    },
    openModal() {
      const m = document.querySelector('.pf-modal');
      if (m) { m.hidden = false; document.body.classList.add('pf-modal-open'); }
    },
    closeModal() {
      const m = document.querySelector('.pf-modal');
      if (m) { m.hidden = true; document.body.classList.remove('pf-modal-open'); }
    },
  });

  const ver = () => (window.PF_V ? '?v=' + window.PF_V : '');
  // mk = make slug, so the results page can load that make's file for the vehicle info card
  const resultsUrl = (v) => RESULTS_URL + '&vid=' + encodeURIComponent(v.vid) + (v.mk ? '&mk=' + encodeURIComponent(v.mk) : '') + '&v=' + encodeURIComponent(v.label);
  const makeCache = {};
  function loadMake(base, slug) {
    if (!makeCache[slug]) makeCache[slug] = fetch(base + 'pf-make-' + slug + '.json' + ver()).then((r) => r.json());
    return makeCache[slug];
  }

  function getMakes() {
    const el = document.querySelector('.pf-makes-data');
    return el ? JSON.parse(el.textContent) : [];
  }

  function opt(select, value, label, placeholder) {
    if (placeholder !== undefined) {
      select.innerHTML = '';
      const o = document.createElement('option');
      o.value = '';
      o.textContent = placeholder;
      select.appendChild(o);
      return;
    }
    const o = document.createElement('option');
    o.value = value;
    o.textContent = label;
    select.appendChild(o);
  }

  function engineLabel(r) {
    // r: [model, gen, yf, yt, type, hp, codes, fuel, body, dt, kt]
    let s = r[4];
    if (r[5]) s += ' ' + r[5] + 'HP';
    if (r[8]) s += ' · ' + r[8];
    if (r[9] && r[9] !== 'FWD') s += ' ' + r[9];
    if (r[6]) s += ' (' + r[6].split(';').slice(0, 2).join(', ') + ')';
    return s;
  }

  // Audi S/RS cars sit under their base model in the vehicle list (A3 + "RS3 quattro");
  // give them their own model entry so shoppers find "RS3 (8V)" directly
  function badgeOf(r, make) {
    if (make !== 'audi') return '';
    const t = r[4].split(' ')[0];
    if (/^(RS\d+|S\d+|SQ\d+|RSQ\d+|TTS)$/i.test(t)) return t.toUpperCase();
    if (r[0] === 'TT' && /\bRS\b/.test(r[4])) return 'TT RS';
    return '';
  }

  function modelLabel(r, make) {
    const code = String(r[13] || '').split(',')[0].trim();
    const gen = r[1] || (/^\d[A-Z][A-Z0-9]$/.test(code) ? code.slice(0, 2) : ''); // VAG platform "8VA" -> "8V"
    return (badgeOf(r, make) || r[0]) + (gen ? ' (' + gen + ')' : '');
  }

  function initPicker(root) {
    if (root.dataset.pfInit) return;
    root.dataset.pfInit = '1';
    const base = root.dataset.base;
    const sMake = root.querySelector('.pf-make');
    const sYear = root.querySelector('.pf-year');
    const sModel = root.querySelector('.pf-model');
    const sEng = root.querySelector('.pf-engine');
    const go = root.querySelector('.pf-go');
    let rows = [];
    let filtered = [];

    opt(sMake, 0, 0, 'Select make');
    getMakes().forEach((m) => opt(sMake, m.s, m.n));
    [sYear, sModel, sEng].forEach((s) => { opt(s, 0, 0, '—'); s.disabled = true; });
    go.disabled = true;

    sMake.addEventListener('change', async () => {
      [sYear, sModel, sEng].forEach((s) => { opt(s, 0, 0, '—'); s.disabled = true; });
      go.disabled = true;
      if (!sMake.value) return;
      rows = (await loadMake(base, sMake.value)).rows;
      let hi = 0, lo = 9999;
      rows.forEach((r) => {
        const yt = r[3] || CURRENT_YEAR;
        if (yt > hi) hi = Math.min(yt, CURRENT_YEAR);
        if (r[2] && r[2] < lo) lo = r[2];
      });
      opt(sYear, 0, 0, 'Select year');
      for (let y = hi; y >= lo; y--) opt(sYear, y, y);
      sYear.disabled = false;
    });

    sYear.addEventListener('change', () => {
      [sModel, sEng].forEach((s) => { opt(s, 0, 0, '—'); s.disabled = true; });
      go.disabled = true;
      if (!sYear.value) return;
      const y = +sYear.value;
      const models = new Map();
      rows.forEach((r) => {
        if (r[2] <= y && y <= (r[3] || CURRENT_YEAR)) models.set(modelLabel(r, sMake.value), true);
      });
      opt(sModel, 0, 0, 'Select model');
      [...models.keys()].sort().forEach((m) => opt(sModel, m, m));
      sModel.disabled = false;
    });

    sModel.addEventListener('change', () => {
      opt(sEng, 0, 0, '—');
      sEng.disabled = true;
      go.disabled = true;
      if (!sModel.value) return;
      const y = +sYear.value;
      filtered = rows.filter((r) => modelLabel(r, sMake.value) === sModel.value && r[2] <= y && y <= (r[3] || CURRENT_YEAR));
      opt(sEng, 0, 0, 'Select engine');
      const seen = {};
      filtered.forEach((r, i) => {
        let lbl = engineLabel(r);
        if (seen[lbl]) lbl += ' [' + r[2] + '–' + (r[3] || 'now') + ']';
        seen[lbl] = true;
        opt(sEng, i, lbl);
      });
      sEng.disabled = false;
    });

    sEng.addEventListener('change', () => { go.disabled = sEng.value === ''; });

    go.addEventListener('click', () => {
      const r = filtered[+sEng.value];
      if (!r) return;
      const v = {
        vid: r[10],
        mk: sMake.value,
        make: sMake.options[sMake.selectedIndex].text,
        year: sYear.value,
        model: sModel.value,
        eng: engineLabel(r),
        // "Audi RS3 (8V) quattro 400HP (2018)": drop the badge from the type when the model already says it
        label: sMake.options[sMake.selectedIndex].text + ' ' + sModel.value + ' ' + (badgeOf(r, sMake.value) && r[4].startsWith(badgeOf(r, sMake.value) + ' ') ? r[4].slice(badgeOf(r, sMake.value).length + 1) : r[4]) + (r[5] ? ' ' + r[5] + 'HP' : '') + ' (' + sYear.value + ')',
      };
      PF.vehicle = v;
      PF.closeModal();
      window.location.href = resultsUrl(v);
    });
  }

  function initChip(root) {
    if (root.dataset.pfInit) return;
    root.dataset.pfInit = '1';
    const btn = root.querySelector('.pf-chip-btn');
    const clear = root.querySelector('.pf-chip-clear');
    const label = root.querySelector('.pf-chip-label');
    function render() {
      const v = PF.vehicle;
      label.textContent = v ? v.label : btn.dataset.empty;
      root.classList.toggle('pf-has-vehicle', !!v);
      clear.hidden = !v;
    }
    btn.addEventListener('click', () => {
      const v = PF.vehicle;
      if (v) window.location.href = resultsUrl(v);
      else PF.openModal();
    });
    root.querySelector('.pf-chip-change')?.addEventListener('click', PF.openModal);
    clear.addEventListener('click', (e) => { e.stopPropagation(); PF.vehicle = null; });
    document.addEventListener('pf:vehicle', render);
    render();
  }

  function initModal(root) {
    if (root.dataset.pfInit) return;
    root.dataset.pfInit = '1';
    root.querySelector('.pf-modal-close').addEventListener('click', PF.closeModal);
    root.addEventListener('click', (e) => { if (e.target === root) PF.closeModal(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') PF.closeModal(); });
  }

  function initBadge(root) {
    if (root.dataset.pfInit) return;
    root.dataset.pfInit = '1';
    let fitIds;
    try { fitIds = new Set(JSON.parse(root.querySelector('.pf-badge-fitdata').textContent)); } catch (e) { return; }
    const fit = root.querySelector('.pf-badge-fit');
    const nofit = root.querySelector('.pf-badge-nofit');
    const select = root.querySelector('.pf-badge-select');
    function render() {
      const v = PF.vehicle;
      root.hidden = false;
      fit.hidden = nofit.hidden = select.hidden = true;
      root.classList.remove('pf-fit', 'pf-nofit');
      if (!v) { select.hidden = false; return; }
      root.querySelectorAll('.pf-badge-vehicle').forEach((el) => { el.textContent = v.label; });
      if (fitIds.has(String(v.vid))) { fit.hidden = false; root.classList.add('pf-fit'); }
      else { nofit.hidden = false; root.classList.add('pf-nofit'); }
    }
    select.addEventListener('click', PF.openModal);
    document.addEventListener('pf:vehicle', render);
    render();
  }

  const FRONT = 1, REAR = 2, NAMED = 8, POA = 16; // index row flags (scripts/build-ktype-index.mjs)
  const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  async function initResults(root) {
    if (root.dataset.pfInit) return;
    root.dataset.pfInit = '1';
    const $ = (s) => root.querySelector(s);
    const base = root.dataset.base;
    const params = new URLSearchParams(location.search);
    // ?vid=<ktype> for one vehicle, or ?vids=a,b,c for a car family (Shop by car tiles: all body styles of one car)
    const kts = [...new Set((params.get('vids') || params.get('vid') || '').split(',').map((x) => x.trim()).filter(Boolean))];
    const kt = kts[0] || '';
    // garage label only when the saved car is the one being viewed — else a bare ?vid= link shows the wrong car
    const g = PF.vehicle;
    const mine = g && String(g.vid) === kt ? g : null;
    const mk = params.get('mk') || (mine && mine.mk) || '';
    const vLabel = params.get('v') || (mine ? mine.label : '');
    const heading = $('.pf-results-vehicle');
    const grid = $('.pf-results-grid');
    const count = $('.pf-results-count');
    const more = $('.pf-results-more');
    if (vLabel) heading.textContent = vLabel;
    $('.pf-change').addEventListener('click', () => PF.openModal());
    if (!kt) { $('.pf-results-empty').hidden = false; return; }

    // rows for every vehicle ID asked for, one row per product (flags OR'd, e.g. "title names the car")
    async function familyRows() {
      const byShard = {};
      // shard = first 4 hex chars of the vid mod 64 (scripts/vid.mjs shardOf)
      kts.forEach((k) => { const n = parseInt(k.slice(0, 4), 16) % 64; (byShard[n] = byShard[n] || []).push(k); });
      const lists = await Promise.all(Object.entries(byShard).map(([n, ks]) =>
        fetch(base + 'pf-idx-' + n + '.json' + ver()).then((r) => r.json()).then((j) => ks.flatMap((k) => j[k] || []))));
      const byHandle = new Map();
      for (const r of lists.flat()) { const e = byHandle.get(r[0]); if (e) e[4] |= r[4]; else byHandle.set(r[0], r.slice()); }
      return [...byHandle.values()];
    }
    let fitRows = [];
    let uniRows = [];
    let dict = { cats: [], brands: [] };
    let icons = {};
    try {
      [fitRows, dict, uniRows, icons] = await Promise.all([
        familyRows(),
        fetch(base + 'pf-dict.json' + ver()).then((r) => r.json()),
        fetch(base + 'pf-universal.json' + ver()).then((r) => r.json()).catch(() => []),
        fetch(base + 'pf-icons.json' + ver()).then((r) => r.json()).catch(() => ({})), // category sketch icons
      ]);
    } catch (e) { /* fall through to empty state */ }
    // car sketch in the banner for the Shop by car cars (pf-dict "sketches": vid -> theme asset car-<id>.webp)
    const sketch = kts.map((k) => (dict.sketches || {})[k]).find(Boolean);
    if (sketch) { const im = $('.pf-vcard-sketch'); im.src = base + 'car-' + sketch + '.webp'; im.hidden = false; }
    if (mk && kts.length === 1) vehicleCard(mk).catch(() => {});
    else if (vLabel) { $('.pf-help-vehicle').textContent = vLabel; $('.pf-tab-car').textContent = vLabel; }
    if (!fitRows.length) $('.pf-results-empty').hidden = false;
    // thin fitment (under 10 parts): point to the hand-built car collection, the universal tab and "send us your reg"
    if (fitRows.length < 10) {
      const thin = $('.pf-thin');
      const cc = kts.map((k) => (dict.carCollections || {})[k]).find(Boolean);
      if (cc) { const a = thin.querySelector('.pf-thin-col'); a.href = '/collections/' + cc[0]; a.textContent = 'See our ' + cc[1] + ' range'; a.hidden = false; }
      thin.querySelector('.pf-thin-more').textContent = uniRows.length ? "Browse our range for this car, check the Universal parts tab, or send us your reg and we'll check fitment." : "Send us your reg and we'll check fitment for you.";
      thin.hidden = false;
    }
    if (!fitRows.length && !uniRows.length) return;
    $('.pf-layout').hidden = false;
    let rows = fitRows; // the set the current tab shows

    // ---- vehicle info card (specs straight from the vehicle list; nothing invented)
    async function vehicleCard(makeSlug) {
      const data = await loadMake(base, makeSlug);
      const r = data.rows.find((x) => x[10] === kt);
      if (!r) return;
      const [model, gen, yf, yt, type, hp, codes, fuel, body, dt, , kw, cc, plat] = r;
      const badge = type.split(' ').find((w) => /[A-Za-z]+\d|^\d+[A-Za-z]+$/.test(w) && !/^\d+\.\d/.test(w)) || '';
      const code = (plat || '').split(',')[0].trim();
      const chassis = gen || (/^\d[A-Z]{2}$/.test(code) ? code.slice(0, 2) : code); // VAG "8VA" -> "8V"
      const name = badge && !model.includes(badge) ? badge : model;
      heading.textContent = [data.make, name, chassis, hp ? hp + 'HP' : ''].filter(Boolean).join(' ');
      $('.pf-help-vehicle').textContent = [name, chassis].filter(Boolean).join(' ');
      $('.pf-tab-car').textContent = [name, chassis].filter(Boolean).join(' ');
      const specs = [
        yf ? yf + '–' + (yt || 'present') : '',
        [cc ? (cc / 1000).toFixed(1) + 'L' : '', fuel].filter(Boolean).join(' '),
        hp ? hp + 'HP' + (kw ? ' / ' + kw + 'kW' : '') : '',
        codes ? 'Engine ' + codes.split(';').join(' / ') : '',
        dt,
      ].filter(Boolean);
      const sibs = data.rows.filter((x) => x[0] === model && x[1] === gen && x[4] === type && x[5] === hp && x[6] === codes && x[2] === yf && x[3] === yt);
      if (sibs.length < 2 && body) specs.push(body);
      const ul = $('.pf-vcard-specs');
      ul.innerHTML = specs.map((s) => '<li>' + esc(s) + '</li>').join('');
      ul.hidden = false;
      if (sibs.length > 1) {
        const sw = $('.pf-body-switch');
        sw.innerHTML = sibs.map((x) => {
          const u = new URLSearchParams(location.search);
          u.set('vid', x[10]);
          return '<a href="?' + esc(u.toString()) + '" class="pf-body-opt' + (x[10] === kt ? ' is-active" aria-current="true' : '') + '">' + esc(x[8]) + '</a>';
        }).join('');
        sw.hidden = false;
      }
    }

    // ---- filter state <-> URL (so Back works and a filtered view can be shared)
    const cats = dict.cats;
    const brandSlug = dict.brands.map(slug);
    const st = {};
    function readUrl() {
      const p = new URLSearchParams(location.search);
      const list = (k) => new Set((p.get(k) || '').split(',').filter(Boolean));
      Object.assign(st, { cat: list('cat'), brand: list('brand'), pos: list('pos'), sort: p.get('sort') || 'best' });
      st.min = p.get('pmin') ? +p.get('pmin') : null;
      st.max = p.get('pmax') ? +p.get('pmax') : null;
      // no fitted parts for this car -> open on the universal tab
      st.tab = p.get('tab') === 'universal' || !fitRows.length ? 'universal' : 'fit';
    }
    function writeUrl() {
      const p = new URLSearchParams(location.search);
      const set = (k, v) => (v ? p.set(k, v) : p.delete(k));
      set('cat', [...st.cat].join(','));
      set('brand', [...st.brand].join(','));
      set('pos', [...st.pos].join(','));
      set('pmin', st.min ?? '');
      set('pmax', st.max ?? '');
      set('sort', st.sort === 'best' ? '' : st.sort);
      set('tab', st.tab === 'universal' && fitRows.length ? 'universal' : '');
      history.pushState(null, '', location.pathname + '?' + p.toString());
    }
    const catId = (r) => (cats[r[1]] || ['other'])[0];
    const test = {
      cat: (r) => !st.cat.size || st.cat.has(catId(r)),
      brand: (r) => !st.brand.size || st.brand.has(brandSlug[r[2]]),
      pos: (r) => !st.pos.size || (st.pos.has('front') && r[4] & FRONT) || (st.pos.has('rear') && r[4] & REAR),
      price: (r) => (st.min == null || r[3] >= st.min * 100) && (st.max == null || (r[3] <= st.max * 100 && !(r[4] & POA))),
    };
    // a group's counts ignore that group's own selection (standard faceting)
    const pass = (r, skip) => Object.keys(test).every((k) => k === skip || test[k](r));
    const tally = (skip, key) => rows.reduce((m, r) => { if (pass(r, skip)) { const k = key(r); m[k] = (m[k] || 0) + 1; } return m; }, {});
    const activeCount = () => st.cat.size + st.brand.size + st.pos.size + (st.min != null || st.max != null ? 1 : 0);

    const sorters = {
      best: (a, b) => (b[4] & NAMED) - (a[4] & NAMED) || a[1] - b[1] || a[3] - b[3],
      'price-asc': (a, b) => a[3] - b[3],
      'price-desc': (a, b) => b[3] - a[3],
      new: (a, b) => b[5] - a[5],
      name: (a, b) => a[0].localeCompare(b[0]),
    };
    // placeholder-priced items (£9,999) always last
    const sortRows = (list) => list.filter((r) => !(r[4] & POA)).sort(sorters[st.sort] || sorters.best).concat(list.filter((r) => r[4] & POA));

    // per-tab totals (recomputed when the tab changes)
    let baseCat = {};
    let baseBrand = {};
    let hasPos = false;
    function useTab() {
      rows = st.tab === 'universal' ? uniRows : fitRows;
      baseCat = rows.reduce((m, r) => ((m[catId(r)] = (m[catId(r)] || 0) + 1), m), {});
      baseBrand = rows.reduce((m, r) => ((m[r[2]] = (m[r[2]] || 0) + 1), m), {});
      hasPos = rows.some((r) => r[4] & (FRONT | REAR));
      const tabs = $('.pf-tabs');
      tabs.hidden = !uniRows.length;
      tabs.querySelectorAll('.pf-tab').forEach((b) => {
        const on = b.dataset.tab === st.tab;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on);
        const n = (b.dataset.tab === 'universal' ? uniRows : fitRows).length;
        b.querySelector('.pf-tab-n').textContent = '(' + n + ')';
        b.disabled = !n;
      });
      $('.pf-uni-note').hidden = st.tab !== 'universal';
    }

    function group(title, key, items, limit) {
      if (!items.length) return '';
      const cut = limit && items.length > limit;
      return '<fieldset class="pf-fg" data-key="' + key + '"><legend>' + title + '</legend>' +
        items.map((it, i) => {
          const on = st[key].has(it.id);
          return '<label class="pf-opt' + (cut && i >= limit && !on ? ' pf-opt-extra' : '') + (it.n ? '' : ' is-empty') + '">' +
            '<input type="checkbox" value="' + esc(it.id) + '"' + (on ? ' checked' : '') + (it.n || on ? '' : ' disabled') + '>' +
            '<span>' + esc(it.label) + '</span><span class="pf-n">' + it.n + '</span></label>';
        }).join('') +
        (cut ? '<button type="button" class="pf-more-opts">Show all ' + items.length + '</button>' : '') + '</fieldset>';
    }
    function renderSide() {
      const cc = tally('cat', catId);
      const bc = tally('brand', (r) => r[2]);
      const pc = { front: 0, rear: 0 };
      rows.forEach((r) => { if (pass(r, 'pos')) { if (r[4] & FRONT) pc.front++; if (r[4] & REAR) pc.rear++; } });
      const open = new Set([...root.querySelectorAll('.pf-fg.pf-expanded')].map((f) => f.dataset.key));
      $('.pf-side-groups').innerHTML =
        group('Category', 'cat', cats.filter(([id]) => baseCat[id]).map(([id, n]) => ({ id, label: n, n: cc[id] || 0 }))) +
        (hasPos ? group('Position', 'pos', [{ id: 'front', label: 'Front', n: pc.front }, { id: 'rear', label: 'Rear', n: pc.rear }]) : '') +
        group('Brand', 'brand', Object.keys(baseBrand).sort((a, b) => baseBrand[b] - baseBrand[a]).map((i) => ({ id: brandSlug[i], label: dict.brands[i], n: bc[i] || 0 })), 8) +
        '<fieldset class="pf-fg pf-fg-price"><legend>Price (£)</legend><div class="pf-price">' +
        '<input type="number" inputmode="numeric" min="0" class="pf-pmin" id="pf-pmin" aria-label="Minimum price" placeholder="Min" value="' + (st.min ?? '') + '">' +
        '<span>to</span><input type="number" inputmode="numeric" min="0" class="pf-pmax" id="pf-pmax" aria-label="Maximum price" placeholder="Max" value="' + (st.max ?? '') + '"></div></fieldset>';
      open.forEach((k) => root.querySelector('.pf-fg[data-key="' + k + '"]')?.classList.add('pf-expanded'));
      root.querySelectorAll('.pf-fg.pf-expanded .pf-more-opts').forEach((b) => b.remove());
    }
    function renderTiles() {
      const nav = $('.pf-tiles');
      nav.innerHTML = cats.filter(([id]) => baseCat[id]).map(([id, n, icon]) =>
        '<button type="button" class="pf-tile haz-draw' + (st.cat.size === 1 && st.cat.has(id) ? ' is-active' : '') + '" data-cat="' + id + '">' +
        (icons[icon] ? '<svg class="haz-ico pf-tile-ico" viewBox="0 0 64 64" aria-hidden="true">' + icons[icon] + '</svg>' : '') +
        '<span class="pf-tile-name">' + esc(n) + '</span><span class="pf-tile-n">' + baseCat[id] + '</span></button>').join('');
      nav.hidden = false;
    }
    function renderChips() {
      const chips = [];
      st.cat.forEach((id) => chips.push(['cat', id, (cats.find((c) => c[0] === id) || [id, id])[1]]));
      st.pos.forEach((id) => chips.push(['pos', id, id === 'front' ? 'Front' : 'Rear']));
      st.brand.forEach((id) => chips.push(['brand', id, dict.brands[brandSlug.indexOf(id)] || id]));
      if (st.min != null || st.max != null) chips.push(['price', '', '£' + (st.min ?? 0) + ' – ' + (st.max != null ? '£' + st.max : 'any')]);
      $('.pf-chips').innerHTML = chips.map(([k, id, label]) =>
        '<button type="button" class="pf-chip-x" data-key="' + k + '" data-id="' + esc(id) + '">' + esc(label) +
        ' <span aria-hidden="true">&times;</span><span class="pf-sr"> (remove filter)</span></button>').join('') +
        (chips.length > 1 ? '<button type="button" class="pf-chip-clear-all pf-clear">Clear all</button>' : '');
    }

    // ---- cards (theme-native, rendered live so price/stock are always current)
    const cache = new Map();
    let shown = [];
    let cursor = 0;
    async function renderPage() {
      more.hidden = true;
      const batch = shown.slice(cursor, cursor + PAGE_SIZE);
      cursor += batch.length;
      const cells = batch.map(() => {
        const d = document.createElement('div');
        d.className = 'pf-cell pf-skeleton';
        grid.appendChild(d);
        return d;
      });
      await Promise.all(batch.map((r, i) => {
        if (!cache.has(r[0])) cache.set(r[0], fetch('/products/' + r[0] + '?sections=pf-card').then((x) => x.json()).then((j) => j['pf-card'] || ''));
        return cache.get(r[0])
          .then((html) => { if (html) { cells[i].innerHTML = html; cells[i].classList.remove('pf-skeleton'); } else cells[i].remove(); })
          .catch(() => cells[i].remove());
      }));
      more.hidden = cursor >= shown.length;
    }

    function apply(push) {
      if (push) writeUrl();
      useTab();
      shown = sortRows(rows.filter((r) => pass(r)));
      renderSide();
      renderTiles();
      renderChips();
      $('.pf-sort').value = st.sort;
      const what = st.tab === 'universal' ? ' universal part' : ' part';
      count.textContent = shown.length === rows.length
        ? rows.length + what + (rows.length === 1 ? '' : 's') + (st.tab === 'universal' ? '' : ' with confirmed fitment')
        : shown.length + ' of ' + rows.length + what + 's';
      $('.pf-nomatch').hidden = shown.length > 0;
      const n = activeCount();
      $('.pf-filter-toggle').textContent = 'Filters' + (n ? ' (' + n + ')' : '');
      grid.innerHTML = '';
      cursor = 0;
      renderPage();
    }

    // ---- events
    const side = $('.pf-side');
    const backdrop = $('.pf-side-backdrop');
    const openSide = (on) => { side.classList.toggle('pf-open', on); backdrop.hidden = !on; document.body.classList.toggle('pf-modal-open', on); };
    $('.pf-filter-toggle').addEventListener('click', () => openSide(true));
    [$('.pf-side-close'), $('.pf-side-done'), backdrop].forEach((el) => el.addEventListener('click', () => openSide(false)));
    side.addEventListener('change', (e) => {
      const t = e.target;
      if (t.matches('input[type=checkbox]')) {
        const set = st[t.closest('.pf-fg').dataset.key];
        if (t.checked) set.add(t.value); else set.delete(t.value);
      } else if (t.matches('.pf-pmin, .pf-pmax')) {
        const v = t.value === '' ? null : Math.max(0, +t.value);
        if (t.matches('.pf-pmin')) st.min = v; else st.max = v;
      } else return;
      apply(true);
    });
    side.addEventListener('click', (e) => {
      const b = e.target.closest('.pf-more-opts');
      if (b) { b.closest('.pf-fg').classList.add('pf-expanded'); b.remove(); }
    });
    // switching tab starts fresh: the two sets have different categories and brands
    $('.pf-tabs').addEventListener('click', (e) => {
      const t = e.target.closest('.pf-tab');
      if (!t || t.dataset.tab === st.tab) return;
      Object.assign(st, { tab: t.dataset.tab, cat: new Set(), brand: new Set(), pos: new Set(), min: null, max: null });
      apply(true);
    });
    $('.pf-tiles').addEventListener('click', (e) => {
      const t = e.target.closest('.pf-tile');
      if (!t) return;
      const id = t.dataset.cat;
      st.cat = st.cat.size === 1 && st.cat.has(id) ? new Set() : new Set([id]);
      apply(true);
      $('.pf-main').scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    root.addEventListener('click', (e) => {
      const x = e.target.closest('.pf-chip-x');
      if (x) {
        if (x.dataset.key === 'price') st.min = st.max = null;
        else st[x.dataset.key].delete(x.dataset.id);
        apply(true);
      } else if (e.target.closest('.pf-clear')) {
        Object.assign(st, { cat: new Set(), brand: new Set(), pos: new Set(), min: null, max: null });
        apply(true);
      }
    });
    $('.pf-sort').addEventListener('change', (e) => { st.sort = e.target.value; apply(true); });
    more.addEventListener('click', renderPage);
    window.addEventListener('popstate', () => { readUrl(); apply(false); });

    readUrl();
    apply(false);
  }

  function initAll() {
    document.querySelectorAll('.pf-picker').forEach(initPicker);
    document.querySelectorAll('.pf-chip').forEach(initChip);
    document.querySelectorAll('.pf-modal').forEach(initModal);
    document.querySelectorAll('.pf-badge').forEach(initBadge);
    document.querySelectorAll('#pf-results').forEach(initResults);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
